import { useEffect, useRef, useState } from "react"
import { TextArea } from "components/ui/inputs/TextArea"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { useGeneralFeedbackStore } from "store/drawer.store"
import { Screenshots } from "components/ui/NegativeFeedbackModal/layout/Screenshots/Screenshots"
import { Form } from "antd"
import { FeedbackThankYou } from "components/ui/FeedbackThankYou/FeedbackThankYou"
import { Spin } from "components/ui/spin/Spin"
import { useFeedbackMutation } from "hooks/api/feedback/feedback.hook"
import { Input } from "components/ui/inputs/Input"
import { v4 as uuidv4 } from "uuid"
import { CloseIcon } from "../icons/uploadbar/CloseIcon"
import type { TDrawingStatus } from "types/drawer.type"
import type { IExtendedFeedbackFile } from "components/ui/NegativeFeedbackModal/NegativeFeedbackModal"
import styles from "./FeedbackPanel.module.scss"

const {
	feedbackPanel,
	header,
	closeButton,
	content,
	textGray,
	screenshotWrapper,
	submitButton,
	textSmall,
	pagesInput,
	textBlack,
	screenshotsArea,
	footer,
	feedbackWrapper,
	thankYouMessageStyle,
	whiteBackground,
	subtitle,
} = styles

type Props = {
	onClose: () => void
	drawingId: number
	processingId: number | string | null
	processingUrl: string
	drawingStatus: TDrawingStatus
	/** Background variant. "white" is used inside the custom webviewer modal; "default" (gray) elsewhere. */
	variant?: "default" | "white"
}

// Max size for image
const imagesMaxSize = import.meta.env.VITE_UPLOADING_FEEDBACK_TOTAL_SIZE
const imagesMaxSizeInMB = imagesMaxSize / (1024 * 1024)

export const FeedbackPanel = ({
	onClose,
	drawingId,
	processingId,
	processingUrl,
	drawingStatus,
	variant = "default",
}: Props) => {
	const [files, setFiles] = useState<IExtendedFeedbackFile[]>([])

	const { setGeneralFeedback } = useGeneralFeedbackStore()
	// Thank-you state is component-local: this panel's submit must not flip another
	// FeedbackPanel / feedback surface that would otherwise share a global store.
	const [thankYouMessage, setThankYouMessage] = useState(false)
	const thankYouTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	// Custom hook to submit feedback
	const { submitFeedback, isFeedbackPending } = useFeedbackMutation({
		onSuccess: () => {
			form.resetFields() // Clear inputs
			setFiles([])
			// The thumbsDown highlight only applies to the default (Documents) feedback flow.
			// The quotation modal ("white") has no thumbs UI, so don't mutate the shared store there.
			if (variant !== "white") {
				setGeneralFeedback("thumbsDown") // Set feedback type
			}
			setThankYouMessage(true) // Show thank you message

			// Reset to form view after 5 seconds
			if (thankYouTimeoutRef.current) {
				clearTimeout(thankYouTimeoutRef.current)
			}
			thankYouTimeoutRef.current = setTimeout(() => {
				setThankYouMessage(false)
			}, 5000)
		},
	})

	const onFilesAdded = (newFiles: IExtendedFeedbackFile[]) => {
		setFiles((prevFiles) =>
			prevFiles.concat(
				newFiles.map((file) =>
					Object.assign(file, {
						preview: URL.createObjectURL(file),
						id: uuidv4(),
					}),
				),
			),
		)
	}

	// Submit feedback to server
	const onSubmitHandle = () => {
		const formData = new FormData()
		// Guard against NaN/empty ids (e.g. processingID missing from the URL) so we never POST
		// the literal string "NaN" — omit the field instead and let the backend validate cleanly.
		if (Number.isFinite(Number(drawingId))) {
			formData.append("drawingId", String(drawingId))
		}
		if (processingId != null && processingId !== "" && Number.isFinite(Number(processingId))) {
			formData.append("processingId", String(processingId))
		}

		// Append pages to feedback text if provided
		let feedbackText = values.feedback
		if (values.pages && values.pages.trim()) {
			feedbackText += `\n[PAGES]\n${values.pages.trim()}`
		}

		formData.append("feedback_text", feedbackText)
		formData.append("type", "NEGATIVE")
		formData.append("processing_url", processingUrl)

		files.forEach((file: IExtendedFeedbackFile) => {
			formData.append("screenshots", file)
		})

		submitFeedback({ formData, processingId, drawingId }, drawingStatus)
	}

	// Removing images
	const removeImageHandle = (id: string) => {
		setFiles((prevFiles) => prevFiles.filter((file) => file.id !== id))
	}

	// Validate screenshots
	const validateScreenshots = () => {
		const maxCount = 10
		const totalImagesSize = files.reduce((acc, file) => acc + file.size, 0)
		const exceedsMaxCount = files.length > maxCount
		const oversizedFiles = totalImagesSize > imagesMaxSize

		if (oversizedFiles) {
			return Promise.reject(new Error(`Total images size must not exceed ${imagesMaxSizeInMB}MB`))
		}
		if (exceedsMaxCount) {
			return Promise.reject(new Error(`You can only add up to ${maxCount} images`))
		}
		return Promise.resolve()
	}

	useEffect(() => {
		return () =>
			files.forEach((file) => {
				if (file.preview) {
					URL.revokeObjectURL(file.preview)
				}
			})
	}, [files])

	// Clear the pending thank-you reset timer if the panel unmounts mid-countdown.
	useEffect(() => {
		return () => {
			if (thankYouTimeoutRef.current) {
				clearTimeout(thankYouTimeoutRef.current)
			}
		}
	}, [])

	return (
		<div className={`${feedbackPanel}${variant === "white" ? ` ${whiteBackground}` : ""}`}>
			<div className={header}>
				<span className={textBlack}>Share feedback</span>
				<button className={closeButton} onClick={onClose} aria-label="Close feedback panel">
					<CloseIcon />
				</button>
			</div>
			{variant === "white" && (
				<p className={subtitle}>Describe the problem, and our team will work on resolving it as quickly as possible.</p>
			)}
			{thankYouMessage ? (
				<div className={thankYouMessageStyle}>
					<FeedbackThankYou
						title="Thank you for your feedback!"
						text="Our team will keep you updated via email with the outcome. Feel free to give feedback as many times as
				necessary."
					/>
				</div>
			) : (
				<Form form={form} style={{ height: "100%" }} onFinish={onSubmitHandle}>
					<div className={feedbackWrapper}>
						<div className={content}>
							{variant !== "white" && <span className={textBlack}>Describe the problem</span>}
							<Form.Item name="feedback" rules={[{ required: true }]}>
								<TextArea placeholder="Share your feedback" minRows={4} />
							</Form.Item>
							<span className={textBlack}>Pages with the issue</span>
							<Form.Item name="pages">
								<Input className={pagesInput} placeholder="Specify pages with the issue" />
							</Form.Item>
							<Form.Item className={screenshotWrapper} name="screenshots" rules={[{ validator: validateScreenshots }]}>
								<Screenshots
									files={files}
									onFilesAdded={onFilesAdded}
									onRemoveFile={removeImageHandle}
									className={screenshotsArea}
								/>
							</Form.Item>
							<span className={`${textGray} ${textSmall}`}>
								{`Upload up to 10 files, total files size must not exceed ${imagesMaxSizeInMB}MB, acceptable file types .png, .jpeg, .jpg`}
							</span>
						</div>
						<div className={footer}>
							<ButtonPrimary className={submitButton} submittable={!isFeedbackPending}>
								{isFeedbackPending ? <Spin size="default" /> : "Send"}
							</ButtonPrimary>
						</div>
					</div>
				</Form>
			)}
		</div>
	)
}
